// `slide-programming-as-translation` + its two reveals, drawn by Mermaid from
// docs/PRESENTATION.md's own mermaid blocks under "Programming languages" / "Vibe coding is
// translation" (task 4.2/4.3):
//
//   step 0 (base):                  My brain -[Vibes]-> English -[Translation]-> Code -[Execution]-> Computer
//   step 1 (reveal-language-range): Code is replaced by the four named languages, each with its own
//                                    translation-difficulty edge in and execution-speed edge out
//                                    (their counterparts from the love chart — C++ is Ancient Greek,
//                                    Python is German, Excel is a hug — are the presenter's line, not
//                                    a sublabel on the node)
//   step 2 (reveal-llm):            an LLM node is inserted between English and the four languages;
//                                    every language now reaches it by the same "Vibes" / "Translation"
//                                    pair rather than its own difficulty label — vibe coding is
//                                    translation via an LLM
//
// Moved onto Mermaid 2026-08-11 for the same reason as the translation chart: a four-way branch laid
// out by hand needed three fixed-height grid columns to keep each language's edge-in, card and
// edge-out on one line, and its arrows still did not reach the nodes they pointed at.

import type { ReactElement } from 'react';
import { SlideFrame } from './SlideFrame';
import { MermaidDiagram } from './MermaidDiagram';
import { DECK_MERMAID_HOT_CLASSDEF, DECK_MERMAID_MACHINE_CLASSDEF } from './deck-theme';
import { PROGRAMMING_CHART_MAX_REVEAL, PROGRAMMING_LANGUAGES } from './content';

/**
 * The chart's Mermaid source at a given reveal step — the slide's test seam (see
 * `translationChartMermaid` for why this shape).
 *
 * A language node is its name alone. It used to carry the human-language counterpart as a `<br/>`
 * sublabel ("C++ / Ancient Greek"); the presenter says that comparison out loud, and on the slide it
 * made a four-way branch of two-line nodes (2026-08-13).
 */
export function programmingChartMermaid(revealStep: number): string {
  const step = Math.max(0, Math.min(revealStep, PROGRAMMING_CHART_MAX_REVEAL));
  const lines = ['flowchart LR', '  brain(["My brain"]) -->|"Vibes"| english["English"]'];

  if (step === 0) {
    lines.push('  english -->|"Translation"| code["Code"]');
    lines.push('  code -->|"Execution"| computer(["Computer"])');
    lines.push(DECK_MERMAID_MACHINE_CLASSDEF, '  class code,computer machine');
    return lines.join('\n');
  }

  const viaLlm = step >= 2;
  if (viaLlm) {
    lines.push('  english -->|"Vibes"| ai(["AI"])');
  }
  const from = viaLlm ? 'ai' : 'english';
  PROGRAMMING_LANGUAGES.forEach((language, index) => {
    const inLabel = viaLlm ? 'Translation' : language.inLabel;
    lines.push(
      `  ${from} -->|"${inLabel}"| ${language.id}["${language.label}"]`,
    );
    // The sink node is declared on the first edge into it and referenced by id after — declaring its
    // shape four times would draw four "Computer" nodes.
    const sink = index === 0 ? 'computer(["Computer"])' : 'computer';
    lines.push(`  ${language.id} -->|"${language.outLabel}"| ${sink}`);
  });

  lines.push(
    DECK_MERMAID_MACHINE_CLASSDEF,
    '  class python,cpp,excel,bpp,computer machine',
  );
  if (viaLlm) lines.push(DECK_MERMAID_HOT_CLASSDEF, '  class ai hot');
  return lines.join('\n');
}

export function ProgrammingChartSlide({ revealStep }: { revealStep: number }): ReactElement {
  return (
    <SlideFrame>
      <MermaidDiagram source={programmingChartMermaid(revealStep)} />
    </SlideFrame>
  );
}
